import { formatMoney } from 'helpers/helpers'

/**
 * Единый разбор записи истории чека (cheques_logs.params) в текст для UI.
 * Тот же разбор, что в кассе (electron_mison_client/src/custom/chequeLogMessage.js):
 * веб показывает историю тех же чеков, и набор событий должен совпадать.
 */

/**
 * Сообщение уходит в dangerouslySetInnerHTML, а интерполяция i18next в проекте
 * не экранирует (escapeValue: false) — экранируем всё, что пришло от пользователя:
 * комментарии, названия скидок и блюд, адреса.
 */
function esc(value) {
	if (value === null || value === undefined) return ''
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
}

/** Строки позиций «x2 Плов» под основным текстом события. */
function productLines(params, t, strike = false) {
	let result = ''
	const products = params?.products
	if (!products || !products.length) return result

	for (let i = 0; i < products.length; i++) {
		const line = t('log_messages.add_products', {
			'product_name': esc(products[i]['name']),
			'product_quantity': products[i]['quantity'],
		})
		result += strike
			? '<div><del>' + line + '</del></div>'
			: '<div>' + line + '</div>'
	}

	return result
}

/** Пометка источника события: внешний заказ, онлайн-меню, облако. */
function sourceSuffix(details, t) {
	if (!details?.source) return ''
	if (details.source === 'external') return ' ' + t('log_messages.source_external')
	if (details.source === 'online_menu') return ' ' + t('log_messages.source_online_menu')
	if (details.source === 'cloud') return ' ' + t('log_messages.source_cloud')
	return ''
}

function orderTypeName(orderType, t) {
	if (Number(orderType) === 1) return t('hall')
	if (Number(orderType) === 2) return t('takeaway')
	if (Number(orderType) === 3) return t('delivery')
	return String(orderType ?? '—')
}

function kitchenStatusName(status, t) {
	const key = `log_messages.kitchen_status_${Number(status)}`
	const name = t(key)
	return name === key ? String(status ?? '') : name
}

export function buildChequeLogMessage(params, t) {
	let message = ''

	if (!params || typeof params !== 'object') return ''

	const details = params['details'] || {}

	if (params['action'] === 'cheque_opened') {
		switch (params['order_type']) {
			case 1:
				message = t('log_messages.hall', {
					'hall_name': esc(details['hall_name']),
					'table_name': esc(details['table_name']),
				})
				break
			case 2:
				message = t('log_messages.takeaway', {})
				break
			case 3: {
				// клиент и телефон появились в логе позже — у старых записей их нет
				const clientName = details['client_name']
				const clientPhone = details['client_phone']
				message = (clientName || clientPhone)
					? t('log_messages.delivery', {
						'client_name': esc(clientName),
						'client_phone': esc(clientPhone),
					})
					: t('log_messages.delivery_no_client')
				break
			}
			default:
				message = t('log_messages.cheque_opened_unknown')
		}
	}

	if (params['action'] === 'cheque_printed') {
		message = t('log_messages.cheque_printed')
	}

	if (params['action'] === 'cheque_printed_cancel') {
		message = t('log_messages.cheque_printed_cancel')
	}

	if (params['action'] === 'cheque_change_waiter') {
		message = t('log_messages.cheque_change_waiter', {
			'name': esc(details['name']),
		})
	}

	if (params['action'] === 'cheque_change_table') {
		message = t('log_messages.cheque_change_table', {
			'from_hall_name': esc(details['from_hall_name']),
			'from_table_name': esc(details['from_table_name']),
			'to_hall_name': esc(details['to_hall_name']),
			'to_table_name': esc(details['to_table_name']),
		})
		// процент обслуживания привязан к столу и меняется вместе с ним
		if (
			details['to_service_percentage'] !== null &&
			details['to_service_percentage'] !== undefined &&
			Number(details['from_service_percentage']) !== Number(details['to_service_percentage'])
		) {
			message += '<div>' + t('log_messages.cheque_service_changed', {
				'from': Number(details['from_service_percentage']) || 0,
				'to': Number(details['to_service_percentage']) || 0,
			}) + '</div>'
		}
	}

	if (params['action'] === 'cheque_changed') {
		message = t('log_messages.products_added') + sourceSuffix(details, t)
		message += productLines(params, t)
	}

	if (params['action'] === 'cheque_delete_product') {
		message = t('log_messages.cheque_delete_product') + sourceSuffix(details, t)
		message += productLines(params, t, true)
		if (details['reason']) {
			message += '<div>' + t('log_messages.deletion_reason', { 'reason': esc(details['reason']) }) + '</div>'
		}
		if (details['comment']) {
			message += '<div>' + t('log_messages.deletion_comment', { 'comment': esc(details['comment']) }) + '</div>'
		}
	}

	if (params['action'] === 'cheque_product_quantity_changed') {
		message = t('log_messages.cheque_product_quantity_changed') + sourceSuffix(details, t)
		const products = params['products'] || []
		for (let i = 0; i < products.length; i++) {
			message += '<div>' + t('log_messages.quantity_change_line', {
				'product_name': esc(products[i]['name']),
				'from': products[i]['old_quantity'] ?? '—',
				'to': products[i]['quantity'],
			}) + '</div>'
		}
	}

	if (params['action'] === 'cheque_transfer_out') {
		message = t('log_messages.transfer_out', {
			'from_hall': esc(details.from_hall_name) || '—',
			'from_table': esc(details.from_table_name) || '—',
			'to_hall': esc(details.to_hall_name) || '—',
			'to_table': esc(details.to_table_name) || '—',
		})
		if (details.mode === 'merge' && details.target_cheque_number != null && details.target_cheque_number !== '') {
			message += ' ' + t('log_messages.transfer_out_suffix_merge', { 'number': details.target_cheque_number })
		} else if (details.mode === 'new_cheque' && details.new_cheque_number != null && details.new_cheque_number !== '') {
			message += ' ' + t('log_messages.transfer_out_suffix_new', { 'number': details.new_cheque_number })
		}
		message += productLines(params, t)
	}

	if (params['action'] === 'cheque_transfer_in') {
		message = t('log_messages.transfer_in', {
			'from_hall': esc(details.from_hall_name) || '—',
			'from_table': esc(details.from_table_name) || '—',
			'cheque_number': details.source_cheque_number ?? '',
		}) + productLines(params, t)
	}

	if (params['action'] === 'cheque_transfer_new_cheque') {
		message = t('log_messages.transfer_new_cheque', {
			'from_hall': esc(details.from_hall_name) || '—',
			'from_table': esc(details.from_table_name) || '—',
			'source_cheque_number': details.source_cheque_number ?? '',
			'to_hall': esc(details.to_hall_name) || '—',
			'to_table': esc(details.to_table_name) || '—',
		}) + productLines(params, t)
	}

	if (params['action'] === 'cheque_split_bill_out') {
		message = t('log_messages.split_bill_out', {
			'new_number': details.new_number ?? '',
		}) + productLines(params, t)
	}

	if (params['action'] === 'cheque_split_bill_new') {
		message = t('log_messages.split_bill_new', {
			'source_number': details.source_number ?? '',
		}) + productLines(params, t)
	}

	if (params['action'] === 'cheque_combine_out') {
		message = t('log_messages.combine_out', {
			'target_number': details.target_number ?? '',
			'to_hall': esc(details.to_hall_name) || '—',
			'to_table': esc(details.to_table_name) || '—',
		}) + productLines(params, t)
	}

	if (params['action'] === 'cheque_combine_in') {
		message = t('log_messages.combine_in', {
			'source_number': details.source_number ?? '',
			'from_hall': esc(details.from_hall_name) || '—',
			'from_table': esc(details.from_table_name) || '—',
		}) + productLines(params, t)
	}

	if (params['action'] === 'cheque_service_changed') {
		message = t('log_messages.cheque_service_changed', {
			'from': Number(details['from']) || 0,
			'to': Number(details['to']) || 0,
		})
	}

	if (params['action'] === 'cheque_people_changed') {
		message = t('log_messages.cheque_people_changed', {
			'from': details['from'] ?? 0,
			'to': details['to'] ?? 0,
		})
	}

	if (params['action'] === 'cheque_comment_changed') {
		const key = details['field'] === 'note'
			? 'log_messages.cheque_note_changed'
			: 'log_messages.cheque_comment_changed'
		message = details['to']
			? t(key, { 'text': esc(details['to']) })
			: t(`${key}_cleared`)
	}

	if (params['action'] === 'cheque_discount_applied') {
		message = Number(details['value']) && details['value_type'] === 'percent'
			? t('log_messages.cheque_discount_applied_percent', {
				'name': esc(details['name']) || '—',
				'value': details['value'],
				'amount': formatMoney(details['amount'] || 0),
			})
			: t('log_messages.cheque_discount_applied_amount', {
				'name': esc(details['name']) || '—',
				'amount': formatMoney(details['amount'] || 0),
			})
		if (details['product_name']) {
			message += ' ' + t('log_messages.discount_on_product', { 'product_name': esc(details['product_name']) })
		}
		if (details['promo_code']) {
			message += ' ' + t('log_messages.discount_promo_code', { 'promo_code': esc(details['promo_code']) })
		}
	}

	if (params['action'] === 'cheque_discount_removed') {
		message = t('log_messages.cheque_discount_removed', {
			'name': esc(details['name']) || '—',
			'amount': formatMoney(details['amount'] || 0),
		})
		if (details['product_name']) {
			message += ' ' + t('log_messages.discount_on_product', { 'product_name': esc(details['product_name']) })
		}
	}

	if (params['action'] === 'cheque_change_order_type') {
		message = t('log_messages.cheque_change_order_type', {
			'from': orderTypeName(details['from'], t),
			'to': orderTypeName(details['to'], t),
		})
		if (details['to_table_name']) {
			message += ' ' + t('log_messages.order_type_table', {
				'hall_name': esc(details['to_hall_name']) || '—',
				'table_name': esc(details['to_table_name']),
			})
		}
	}

	if (params['action'] === 'cheque_change_payment_type') {
		message = t('log_messages.cheque_change_payment_type', {
			'from': esc(details['from']) || '—',
			'to': esc(details['to']) || '—',
		}) + sourceSuffix(details, t)
	}

	if (params['action'] === 'cheque_deleted') {
		message = t('log_messages.cheque_deleted')
		if (details['is_write_off']) message += ' ' + t('log_messages.cheque_deleted_write_off')
		if (details['reason']) {
			message += '<div>' + t('log_messages.deletion_reason', { 'reason': esc(details['reason']) }) + '</div>'
		}
		if (details['comment']) {
			message += '<div>' + t('log_messages.deletion_comment', { 'comment': esc(details['comment']) }) + '</div>'
		}
		message += productLines(params, t, true)
	}

	if (params['action'] === 'cheque_closed_deleted') {
		message = t('log_messages.cheque_closed_deleted') + sourceSuffix(details, t)
	}

	if (params['action'] === 'cheque_returned') {
		message = t('log_messages.cheque_returned', {
			'amount': formatMoney(details['amount'] || 0),
		}) + productLines(params, t, true)
	}

	if (params['action'] === 'cheque_courier_added') {
		message = t('log_messages.cheque_courier_added', { 'name': esc(details['name']) || '—' })
	}

	if (params['action'] === 'cheque_courier_removed') {
		message = t('log_messages.cheque_courier_removed', { 'name': esc(details['name']) || '—' })
	}

	if (params['action'] === 'cheque_external_courier_added') {
		message = t('log_messages.cheque_external_courier_added')
	}

	if (params['action'] === 'cheque_external_courier_cancelled') {
		message = t('log_messages.cheque_external_courier_cancelled')
	}

	if (params['action'] === 'cheque_address_changed') {
		message = t('log_messages.cheque_address_changed', {
			'address': esc(details['address']) || '—',
		})
	}

	if (params['action'] === 'cheque_correction') {
		message = t('log_messages.cheque_correction', {
			'amount': formatMoney(details['total_amount_service'] || 0),
		})
	}

	if (params['action'] === 'cheque_kitchen_status') {
		message = t('log_messages.cheque_kitchen_status', {
			'status': kitchenStatusName(details['status'], t),
		})
		message += productLines(params, t)
	}

	if (params['action'] === 'cheque_monitor_status') {
		const statusKey = `log_messages.monitor_status_${Number(details['status'])}`
		const statusName = t(statusKey)
		message = t('log_messages.cheque_monitor_status', {
			'status': statusName === statusKey ? String(details['status'] ?? '') : statusName,
		})
	}

	if (params['action'] === 'cheque_auto_confirmed') {
		message = t('log_messages.cheque_auto_confirmed') + sourceSuffix(details, t)
	}

	if (params['action'] === 'cheque_product_given') {
		message = details['is_product_given']
			? t('log_messages.cheque_product_given')
			: t('log_messages.cheque_product_given_cancel')
		message += productLines(params, t)
	}

	if (params['action'] === 'cheque_invoice_issued') {
		message = t('log_messages.cheque_invoice_issued', {
			'name': esc(details['payment_type_name']) || '—',
			'amount': formatMoney(details['amount'] || 0),
		})
	}

	if (params['action'] === 'cheque_back_to_work') {
		message = t('log_messages.cheque_back_to_work')
	}

	if (params['action'] === 'cheque_closed') {
		message = t('log_messages.cheque_closed') + sourceSuffix(details, t)

		if (details['total_amount_service'] !== undefined) {
			message += '<div>' + t('log_messages.closed_total', {
				'amount': formatMoney(details['total_amount_service'] || 0),
			}) + '</div>'
		}
		if (Number(details['discount'])) {
			message += '<div>' + t('log_messages.closed_discount', {
				'amount': formatMoney(details['discount']),
			}) + '</div>'
		}
		if (Number(details['service_amount'])) {
			message += '<div>' + t('log_messages.closed_service', {
				'amount': formatMoney(details['service_amount']),
				'percentage': Number(details['service_percentage']) || 0,
			}) + '</div>'
		}
		const payments = details['payments'] || []
		for (let i = 0; i < payments.length; i++) {
			message += '<div>' + t('log_messages.closed_payment', {
				'name': esc(payments[i]['name']) || '—',
				'amount': formatMoney(payments[i]['amount'] || 0),
			}) + '</div>'
		}
	}

	// неизвестное событие всё равно показываем — лучше «сырой» тип, чем пустая строка
	if (!message && params['action']) {
		message = String(params['action'])
	}

	return message
}
